import { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  usePropertyQuery,
  useUpdatePropertyMutation,
  useWithdrawPropertyMutation,
} from '@/api/endpoints';
import { apiErrorMessage, apiFieldErrors } from '@/api/baseApi';
import { formatDate, formatMoney } from '@/lib/format';
import {
  Alert, Badge, Button, ConfirmDialog, ErrorState, LoadingState, Modal, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import {
  PropertyFields,
  formFromProperty,
  propertyIsComplete,
  propertyPayload,
  type PropertyFormState,
} from '@/components/PropertyFields';

const STATUS_TONE: Record<string, 'neutral' | 'brand' | 'success' | 'warning' | 'danger'> = {
  AVAILABLE: 'success',
  RESERVED: 'warning',
  SOLD: 'brand',
  WITHDRAWN: 'neutral',
  AGREED: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'danger',
};

/**
 * One property: what the title deed says, what it is worth, and what has been
 * sold against it.
 *
 * Editing and removing live here rather than as icons in the list, because
 * both need the whole record in front of you first — a plot is identified by
 * its title number and its beacons, not by a row in a table.
 */
export function PropertyDetailModal({
  propertyId,
  onClose,
}: {
  propertyId: string;
  onClose: () => void;
}) {
  const format = useFormat();
  const { data: property, isLoading, isError, error, refetch } = usePropertyQuery(propertyId);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<PropertyFormState | null>(null);
  const [removing, setRemoving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const [update, updateState] = useUpdatePropertyMutation();
  const [withdraw, withdrawState] = useWithdrawPropertyMutation();

  const fieldErrors = apiFieldErrors(updateState.error);

  const startEditing = (): void => {
    if (!property) return;
    setFailure(null);
    setForm(formFromProperty(property));
    setEditing(true);
  };

  const save = async (): Promise<void> => {
    if (!form) return;
    setFailure(null);
    try {
      await update({ id: propertyId, body: propertyPayload(form, 'edit') }).unwrap();
      setEditing(false);
    } catch (problem) {
      setFailure(apiErrorMessage(problem, 'That property could not be saved'));
    }
  };

  const remove = async (): Promise<void> => {
    setFailure(null);
    try {
      await withdraw(propertyId).unwrap();
      setRemoving(false);
      onClose();
    } catch (problem) {
      // The usual refusal is a live sale pointing at it, and the server says
      // which — so it is repeated rather than replaced with something vaguer.
      setRemoving(false);
      setFailure(apiErrorMessage(problem, 'That property could not be removed'));
    }
  };

  const sales = property?.sales ?? [];

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={property ? `${property.reference} — ${property.name}` : 'Property'}
      description={
        property
          ? [property.kind.toLowerCase(), property.location, property.district]
              .filter(Boolean)
              .join(' · ')
          : undefined
      }
      footer={
        editing ? (
          <>
            <Button variant="outline" onClick={() => setEditing(false)} disabled={updateState.isLoading}>
              Cancel
            </Button>
            <Button
              loading={updateState.isLoading}
              disabled={!form || !propertyIsComplete(form)}
              onClick={() => void save()}
            >
              Save changes
            </Button>
          </>
        ) : (
          <div className="flex w-full items-center justify-between gap-2">
            <Can permission={P.PROPERTY_MANAGE}>
              <Button
                variant="ghost"
                icon={Trash2}
                onClick={() => setRemoving(true)}
                disabled={!property}
              >
                Remove
              </Button>
            </Can>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Can permission={P.PROPERTY_MANAGE}>
                <Button icon={Pencil} onClick={startEditing} disabled={!property}>
                  Edit property
                </Button>
              </Can>
            </div>
          </div>
        )
      }
    >
      {failure && <div className="mb-4"><Alert tone="danger">{failure}</Alert></div>}

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
      ) : !property ? null : editing && form ? (
        <PropertyFields value={form} onChange={setForm} errors={fieldErrors} showStatus />
      ) : (
        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <Figure label="Asking price" value={formatMoney(property.askingPrice, format)} />
            <Figure
              label="Size"
              value={
                property.area
                  ? `${property.area} ${property.areaUnit.toLowerCase().replace('_', ' ')}`
                  : '—'
              }
            />
            <div className="rounded-lg border border-subtle p-3">
              <p className="text-xs text-muted">Status</p>
              <p className="mt-1">
                <Badge tone={STATUS_TONE[property.status] ?? 'neutral'}>
                  {property.status.toLowerCase()}
                </Badge>
              </p>
            </div>
          </div>

          <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            <Detail label="Title number" value={property.titleNumber} empty="no title on file" />
            <Detail label="Plot number" value={property.plotNumber} />
            <Detail label="Block number" value={property.blockNumber} />
            <Detail label="Location" value={property.location} />
            <Detail label="District" value={property.district} />
            <Detail label="Coordinates" value={property.coordinates} empty="no beacons recorded" />
            <Can permission={P.PRODUCT_COST_VIEW}>
              <Detail
                label="What it cost us"
                value={property.acquisitionCost ? formatMoney(property.acquisitionCost, format) : null}
              />
            </Can>
          </div>

          {property.description && (
            <div>
              <p className="text-xs text-muted">Description</p>
              <p className="mt-1 text-sm">{property.description}</p>
            </div>
          )}

          {property.notes && (
            <div>
              <p className="text-xs text-muted">Internal notes</p>
              <p className="mt-1 text-sm text-secondary">{property.notes}</p>
            </div>
          )}

          <div>
            <h3 className="mb-2 text-sm font-medium">Sales against this property</h3>
            {sales.length === 0 ? (
              <p className="text-sm text-muted">
                None yet. A property can be removed only while nothing has been sold against it.
              </p>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Sale</Th>
                    <Th>Buyer</Th>
                    <Th>Agreed</Th>
                    <Th numeric>Price</Th>
                    <Th numeric>Outstanding</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale) => (
                    <tr key={sale.id}>
                      <Td className="font-medium">{sale.number}</Td>
                      <Td className="text-secondary">{sale.customer.name}</Td>
                      <Td className="text-secondary">{formatDate(sale.agreedAt, format)}</Td>
                      <Td numeric>{formatMoney(sale.price, format)}</Td>
                      <Td numeric className={Number(sale.dueAmount) > 0 ? 'text-amber-600' : ''}>
                        {formatMoney(sale.dueAmount, format)}
                      </Td>
                      <Td>
                        <Badge tone={STATUS_TONE[sale.status] ?? 'neutral'}>
                          {sale.status.toLowerCase()}
                        </Badge>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={removing}
        onClose={() => setRemoving(false)}
        onConfirm={() => void remove()}
        loading={withdrawState.isLoading}
        title="Remove this property?"
        confirmLabel="Remove it"
        message={
          'It stops being listed and cannot be sold. Nothing is destroyed — the record and any '
          + 'cancelled sales against it remain. To keep it on the books but off the market, set '
          + 'its status to withdrawn instead.'
        }
      />
    </Modal>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-subtle p-3">
      <p className="text-xs text-muted">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular">{value}</p>
    </div>
  );
}

function Detail({
  label,
  value,
  empty = '—',
}: {
  label: string;
  value?: string | null;
  empty?: string;
}) {
  return (
    <div className="flex justify-between gap-3 border-b border-subtle py-1.5 text-sm">
      <span className="text-muted">{label}</span>
      <span className={value ? 'text-right' : 'text-right text-muted'}>{value || empty}</span>
    </div>
  );
}
